import React, { useState, useEffect } from 'react';
import { getSettings, updateSettings } from '../services/api';
import toast from 'react-hot-toast';

const AdminSettings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [originalSettings, setOriginalSettings] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await getSettings();
      console.log('Raw Settings Response:', response);
      
      let settingsData = {};
      
      if (response && response.data) {
        // IMPORTANT: Use the OUTER data (the one with the updated values)
        // The inner data is nested and may contain old values
        const outerData = response.data;
        
        // Check if outer data has a nested data property
        if (outerData.data && typeof outerData.data === 'object') {
          // We want the outer data, not the inner one
          // Copy all properties from outerData except the nested 'data' property
          const cleanData = {};
          Object.keys(outerData).forEach(key => {
            if (key !== 'data' && key !== 'success' && key !== 'message') {
              cleanData[key] = outerData[key];
            }
          });
          settingsData = cleanData;
        } else {
          settingsData = outerData;
        }
      } else if (response) {
        settingsData = response;
      }
      
      // Ensure socialLinks is a string
      if (settingsData.socialLinks && typeof settingsData.socialLinks === 'object') {
        settingsData.socialLinks = JSON.stringify(settingsData.socialLinks);
      }
      
      console.log('Extracted Settings Data:', settingsData);
      
      setSettings(settingsData);
      setOriginalSettings(settingsData);
      
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Error fetching settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Create a clean settings object - ONLY the fields you want to update
      const cleanSettings = {
        siteTitle: settings.siteTitle || '',
        siteDescription: settings.siteDescription || '',
        tagline: settings.tagline || '',
        companyName: settings.companyName || '',
        contactEmail: settings.contactEmail || '',
        phone: settings.phone || '',
        address: settings.address || '',
        logoUrl: settings.logoUrl || '',
        headerBackgroundColor: settings.headerBackgroundColor || '#FFFFFF',
        headerTextColor: settings.headerTextColor || '#0A192F',
        footerBackgroundColor: settings.footerBackgroundColor || '#0A192F',
        footerTextColor: settings.footerTextColor || '#A0B4C8',
        footerText: settings.footerText || '',
        copyrightText: settings.copyrightText || '',
        socialLinks: settings.socialLinks || '{"linkedin":"#","twitter":"#","facebook":"#","instagram":"#"}'
      };
      
      // Ensure socialLinks is a valid string
      if (cleanSettings.socialLinks && typeof cleanSettings.socialLinks === 'object') {
        cleanSettings.socialLinks = JSON.stringify(cleanSettings.socialLinks);
      }
      
      // Validate socialLinks JSON
      if (cleanSettings.socialLinks) {
        try {
          JSON.parse(cleanSettings.socialLinks);
        } catch (e) {
          toast.error('Invalid JSON format in Social Links');
          setSaving(false);
          return;
        }
      }
      
      console.log('Sending clean settings to API:', cleanSettings);
      
      const response = await updateSettings(cleanSettings);
      console.log('Update response:', response);
      
      if (response && response.success) {
        toast.success('Settings updated successfully');
        // Refresh to get latest data
        await fetchSettings();
      } else {
        toast.error(response?.message || 'Failed to update settings');
      }
      
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error(error.message || 'Error updating settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings to the last saved values?')) {
      setSettings(originalSettings);
      toast.info('Settings reset to last saved values');
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: 'fas fa-globe' },
    { id: 'contact', label: 'Contact', icon: 'fas fa-address-card' },
    { id: 'appearance', label: 'Appearance', icon: 'fas fa-palette' },
    { id: 'footer', label: 'Footer', icon: 'fas fa-file-alt' },
    { id: 'social', label: 'Social Links', icon: 'fas fa-share-alt' }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div className="spinner" style={{
          width: '40px',
          height: '40px',
          border: '4px solid #E2E8F0',
          borderTopColor: '#2C7A7B',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 1rem'
        }} />
        <p>Loading settings...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#0A192F', marginBottom: '0.5rem' }}>
          <i className="fas fa-cog" style={{ color: '#2C7A7B', marginRight: '0.5rem' }}></i>
          Website Settings
        </h2>
        <p style={{ color: '#5A6E7F' }}>Manage your website configuration, contact information, and appearance</p>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        borderBottom: '2px solid #E2E8F0',
        marginBottom: '2rem',
        flexWrap: 'wrap'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === tab.id ? '#2C7A7B' : 'transparent',
              color: activeTab === tab.id ? 'white' : '#5A6E7F',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s'
            }}
          >
            <i className={tab.icon}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Settings Form */}
      <form onSubmit={handleSave}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          
          {/* General Settings Tab */}
          {activeTab === 'general' && (
            <div>
              <h3 style={{ marginBottom: '1.5rem', color: '#0A192F' }}>
                <i className="fas fa-globe" style={{ color: '#2C7A7B', marginRight: '0.5rem' }}></i>
                General Settings
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem' }}>
                <SettingField
                  label="Site Title"
                  name="siteTitle"
                  value={settings.siteTitle || ''}
                  onChange={handleChange}
                  placeholder="INTELIHEADS INTERNET Pvt. Ltd"
                />
                <SettingField
                  label="Site Description"
                  name="siteDescription"
                  value={settings.siteDescription || ''}
                  onChange={handleChange}
                  placeholder="Power Systems & Engineering Simulation"
                />
                <SettingField
                  label="Tagline"
                  name="tagline"
                  value={settings.tagline || ''}
                  onChange={handleChange}
                  placeholder="AI & Robotics in Education"
                />
                <SettingField
                  label="Company Name"
                  name="companyName"
                  value={settings.companyName || ''}
                  onChange={handleChange}
                  placeholder="INTELIHEADS INTERNET Pvt. Ltd"
                />
                <SettingField
                  label="Logo URL"
                  name="logoUrl"
                  value={settings.logoUrl || ''}
                  onChange={handleChange}
                  placeholder="https://example.com/logo.png"
                />
              </div>
            </div>
          )}

          {/* Contact Settings Tab */}
          {activeTab === 'contact' && (
            <div>
              <h3 style={{ marginBottom: '1.5rem', color: '#0A192F' }}>
                <i className="fas fa-address-card" style={{ color: '#2C7A7B', marginRight: '0.5rem' }}></i>
                Contact Information
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem' }}>
                <SettingField
                  label="Contact Email"
                  name="contactEmail"
                  type="email"
                  value={settings.contactEmail || ''}
                  onChange={handleChange}
                  placeholder="global@inteliheads.com"
                />
                <SettingField
                  label="Phone Number"
                  name="phone"
                  value={settings.phone || ''}
                  onChange={handleChange}
                  placeholder="+91-8076842489"
                />
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0A192F' }}>
                    <i className="fas fa-map-marker-alt" style={{ color: '#2C7A7B', marginRight: '0.5rem' }}></i>
                    Address
                  </label>
                  <textarea
                    value={settings.address || ''}
                    onChange={(e) => handleChange('address', e.target.value)}
                    rows="3"
                    placeholder="Enter your full address"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontFamily: 'inherit',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2C7A7B'}
                    onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                  />
                  <small style={{ color: '#5A6E7F' }}>
                    <i className="fas fa-info-circle"></i> This address will be displayed in the footer
                  </small>
                </div>
              </div>
            </div>
          )}

          {/* Appearance Settings Tab */}
          {activeTab === 'appearance' && (
            <div>
              <h3 style={{ marginBottom: '1.5rem', color: '#0A192F' }}>
                <i className="fas fa-palette" style={{ color: '#2C7A7B', marginRight: '0.5rem' }}></i>
                Appearance Settings
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem' }}>
                <ColorField
                  label="Header Background Color"
                  name="headerBackgroundColor"
                  value={settings.headerBackgroundColor || '#FFFFFF'}
                  onChange={handleChange}
                />
                <ColorField
                  label="Header Text Color"
                  name="headerTextColor"
                  value={settings.headerTextColor || '#0A192F'}
                  onChange={handleChange}
                />
                <ColorField
                  label="Footer Background Color"
                  name="footerBackgroundColor"
                  value={settings.footerBackgroundColor || '#0A192F'}
                  onChange={handleChange}
                />
                <ColorField
                  label="Footer Text Color"
                  name="footerTextColor"
                  value={settings.footerTextColor || '#A0B4C8'}
                  onChange={handleChange}
                />
              </div>
            </div>
          )}

          {/* Footer Settings Tab */}
          {activeTab === 'footer' && (
            <div>
              <h3 style={{ marginBottom: '1.5rem', color: '#0A192F' }}>
                <i className="fas fa-file-alt" style={{ color: '#2C7A7B', marginRight: '0.5rem' }}></i>
                Footer Settings
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0A192F' }}>
                    <i className="fas fa-align-left" style={{ color: '#2C7A7B', marginRight: '0.5rem' }}></i>
                    Footer Text
                  </label>
                  <textarea
                    value={settings.footerText || ''}
                    onChange={(e) => handleChange('footerText', e.target.value)}
                    rows="3"
                    placeholder="Enter footer description text"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontFamily: 'inherit'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2C7A7B'}
                    onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                  />
                </div>
                <SettingField
                  label="Copyright Text"
                  name="copyrightText"
                  value={settings.copyrightText || ''}
                  onChange={handleChange}
                  placeholder="© 2026 INTELIHEADS INTERNET Pvt. Ltd — All Rights Reserved."
                />
              </div>
            </div>
          )}

          {/* Social Links Tab */}
          {activeTab === 'social' && (
            <div>
              <h3 style={{ marginBottom: '1.5rem', color: '#0A192F' }}>
                <i className="fas fa-share-alt" style={{ color: '#2C7A7B', marginRight: '0.5rem' }}></i>
                Social Media Links
              </h3>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0A192F' }}>
                  <i className="fas fa-code" style={{ color: '#2C7A7B', marginRight: '0.5rem' }}></i>
                  Social Links (JSON)
                </label>
                <textarea
                  value={typeof settings.socialLinks === 'string' ? settings.socialLinks : JSON.stringify({
                    linkedin: '#',
                    twitter: '#',
                    facebook: '#',
                    instagram: '#'
                  }, null, 2)}
                  onChange={(e) => handleChange('socialLinks', e.target.value)}
                  rows="6"
                  placeholder='{"linkedin":"#","twitter":"#","facebook":"#","instagram":"#"}'
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    fontFamily: 'monospace',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#2C7A7B'}
                  onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                />
                <small style={{ color: '#5A6E7F', display: 'block', marginTop: '0.25rem' }}>
                  <i className="fas fa-info-circle"></i> Enter valid JSON format for social media links
                </small>
              </div>
              
              {/* Social Links Preview */}
              <div style={{
                background: '#F7F9FC',
                padding: '1rem',
                borderRadius: '12px',
                marginTop: '1rem'
              }}>
                <h4 style={{ marginBottom: '0.75rem', color: '#0A192F', fontSize: '0.9rem' }}>Preview:</h4>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {(() => {
                    try {
                      const socialStr = typeof settings.socialLinks === 'string' 
                        ? settings.socialLinks 
                        : JSON.stringify(settings.socialLinks || {});
                      const links = JSON.parse(socialStr);
                      
                      const platforms = [
                        { key: 'linkedin', icon: 'fab fa-linkedin', color: '#0077B5' },
                        { key: 'twitter', icon: 'fab fa-twitter', color: '#1DA1F2' },
                        { key: 'facebook', icon: 'fab fa-facebook', color: '#1877F2' },
                        { key: 'instagram', icon: 'fab fa-instagram', color: '#E4405F' }
                      ];
                      
                      return platforms.map((platform) => (
                        <div key={platform.key} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.25rem 0.75rem',
                          background: 'white',
                          borderRadius: '20px',
                          border: '1px solid #E2E8F0'
                        }}>
                          <i className={platform.icon} style={{ color: platform.color }}></i>
                          <span style={{ fontSize: '0.8rem', color: '#5A6E7F' }}>
                            {links[platform.key] && links[platform.key] !== '#' ? '✓ Set' : 'Not set'}
                          </span>
                        </div>
                      ));
                    } catch (e) {
                      return <span style={{ color: '#DC2626', fontSize: '0.8rem' }}>⚠️ Invalid JSON format</span>;
                    }
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{
            marginTop: '2rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid #E2E8F0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div>
              <button
                type="button"
                onClick={handleReset}
                style={{
                  padding: '0.6rem 1.2rem',
                  background: 'transparent',
                  color: '#5A6E7F',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#F7F9FC';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <i className="fas fa-undo"></i> Reset
              </button>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: '0.75rem 2rem',
                  background: '#2C7A7B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  transition: 'all 0.2s',
                  opacity: saving ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!saving) e.currentTarget.style.background = '#236B6C';
                }}
                onMouseLeave={(e) => {
                  if (!saving) e.currentTarget.style.background = '#2C7A7B';
                }}
              >
                {saving ? (
                  <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                ) : (
                  <><i className="fas fa-save"></i> Save All Settings</>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

// Setting Field Component
const SettingField = ({ label, name, type = 'text', value, onChange, placeholder }) => {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0A192F' }}>
        {label}
      </label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '0.75rem',
          border: '1px solid #E2E8F0',
          borderRadius: '8px',
          fontSize: '0.95rem',
          transition: 'border-color 0.2s'
        }}
        onFocus={(e) => e.target.style.borderColor = '#2C7A7B'}
        onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
      />
    </div>
  );
};

// Color Field Component
const ColorField = ({ label, name, value, onChange }) => {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0A192F' }}>
        {label}
      </label>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <input
          type="color"
          value={value || '#FFFFFF'}
          onChange={(e) => onChange(name, e.target.value)}
          style={{
            width: '60px',
            height: '45px',
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            padding: '0.25rem',
            cursor: 'pointer'
          }}
        />
        <input
          type="text"
          value={value || '#FFFFFF'}
          onChange={(e) => onChange(name, e.target.value)}
          style={{
            flex: 1,
            padding: '0.5rem',
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '0.85rem'
          }}
          onFocus={(e) => e.target.style.borderColor = '#2C7A7B'}
          onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
        />
      </div>
    </div>
  );
};

export default AdminSettings;